import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { api, formatApiError, fileUrl } from '@/lib/api';
import { CATEGORY_LIST, CATEGORY_MAX_MILESTONES, colorForCategory, textOn, categorySlug } from '@/lib/categories';
import { toast } from 'sonner';
import {
    ArrowLeft, Save, Upload, Image as ImageIcon, FileText, Loader2, X,
} from 'lucide-react';

const EMPTY = {
    firstName: '', lastName: '', companyName: '', email: '',
    awards: [], totalMilestones: 0,
    photoUrl: '', bio: '',
    links: { website: '', instagram: '', linkedin: '', twitter: '' },
    certificateUrl: '', featured: false,
    hasAcceptanceSpeech: false, acceptanceSpeechEmbed: '',
    awardYear: '',
};

export default function AdminRecipientForm() {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();

    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingCert, setUploadingCert] = useState(false);

    const photoRef = useRef(null);
    const certRef = useRef(null);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/admin/recipients/${id}`);
                setForm({ ...EMPTY, ...data, links: { ...EMPTY.links, ...(data.links || {}) } });
            } catch (e) {
                toast.error(formatApiError(e));
                navigate('/admin');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isEdit, navigate]);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const setLink = (patch) => setForm((f) => ({ ...f, links: { ...f.links, ...patch } }));

    const toggleAward = (name) => {
        setForm((f) => ({
            ...f,
            awards: f.awards.includes(name)
                ? f.awards.filter((a) => a !== name)
                : [...f.awards, name],
        }));
    };

    const handleFile = async (file, kind) => {
        if (!file) return;
        const setBusy = kind === 'photo' ? setUploadingPhoto : setUploadingCert;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('kind', kind);
            const { data } = await api.post('/admin/upload', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (kind === 'photo') set({ photoUrl: data.url });
            else set({ certificateUrl: data.url });
            toast.success(kind === 'photo' ? 'Photo uploaded' : 'Certificate uploaded');
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.firstName.trim() || !form.lastName.trim()) {
            toast.error('First and last name are required');
            return;
        }
        setSaving(true);
        try {
            // totalMilestones is derived server-side from awards; don't send stale value.
            const { totalMilestones, ...rest } = form;
            // awardYear: coerce blank → null, digits → int (backend expects Optional[int])
            const yr = String(rest.awardYear ?? '').trim();
            rest.awardYear = yr === '' ? null : Number(yr);
            const body = rest;
            if (isEdit) {
                await api.patch(`/admin/recipients/${id}`, body);
                toast.success('Recipient updated');
            } else {
                await api.post('/admin/recipients', body);
                toast.success('Recipient created');
            }
            navigate('/admin');
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-black">
                <Navbar variant="admin" />
                <div className="max-w-5xl mx-auto px-6 py-24 flex items-center justify-center text-white/60">
                    <Loader2 className="animate-spin" size={20} /><span className="ml-3 text-xs uppercase tracking-[0.2em]">Loading</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black">
            <Navbar variant="admin" />

            <form
                onSubmit={onSubmit}
                data-testid="recipient-form"
                className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14"
            >
                <Link
                    to="/admin"
                    data-testid="form-back-link"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60 hover:text-white transition-colors mb-6"
                >
                    <ArrowLeft size={14} /> Back to dashboard
                </Link>

                <div className="flex items-center justify-between gap-4 mb-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.28em] text-un-water font-semibold">
                            {isEdit ? 'Edit' : 'New'}
                        </div>
                        <h1 className="mt-2 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl">
                            {isEdit ? 'Edit recipient' : 'Add recipient'}
                        </h1>
                    </div>
                    <button
                        data-testid="form-submit-btn"
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-full bg-un-lime text-black font-semibold px-5 py-3 hover:bg-white transition-colors disabled:opacity-60"
                    >
                        {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                        {saving ? 'Saving…' : 'Save recipient'}
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left col: media */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* Photo */}
                        <Panel title="Photo">
                            <div
                                className="rounded-full border border-dashed border-white/15 bg-black aspect-square flex items-center justify-center overflow-hidden mx-auto max-w-xs"
                            >
                                {form.photoUrl ? (
                                    <img
                                        data-testid="form-photo-preview"
                                        src={fileUrl(form.photoUrl)}
                                        alt="Recipient"
                                        className="h-full w-full object-cover rounded-full"
                                    />
                                ) : (
                                    <div className="text-white/40 flex flex-col items-center">
                                        <ImageIcon size={24} />
                                        <span className="mt-2 text-xs uppercase tracking-[0.2em]">No photo</span>
                                    </div>
                                )}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <input
                                    ref={photoRef}
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    data-testid="form-photo-input"
                                    onChange={(e) => handleFile(e.target.files?.[0], 'photo')}
                                />
                                <button
                                    type="button"
                                    onClick={() => photoRef.current?.click()}
                                    data-testid="form-photo-upload-btn"
                                    disabled={uploadingPhoto}
                                    className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-4 py-2 hover:bg-un-lime transition-colors disabled:opacity-60"
                                >
                                    {uploadingPhoto ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                                    {uploadingPhoto ? 'Uploading' : (form.photoUrl ? 'Replace' : 'Upload photo')}
                                </button>
                                {form.photoUrl && (
                                    <button
                                        type="button"
                                        onClick={() => set({ photoUrl: '' })}
                                        data-testid="form-photo-clear-btn"
                                        className="inline-flex items-center gap-1 rounded-full border border-white/20 text-white/80 text-sm px-3 py-2 hover:border-red-500 hover:text-red-400 transition-colors"
                                    >
                                        <X size={14} /> Clear
                                    </button>
                                )}
                            </div>
                        </Panel>

                        {/* Certificate */}
                        <Panel title="Certificate (PDF or image)">
                            <div className="rounded-2xl border border-dashed border-white/15 bg-black p-6 text-center">
                                <FileText className="mx-auto text-white/40" size={22} />
                                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-white/50">
                                    {form.certificateUrl ? 'Certificate attached' : 'No certificate uploaded'}
                                </div>
                                {form.certificateUrl && (
                                    <a
                                        href={fileUrl(form.certificateUrl)}
                                        target="_blank" rel="noreferrer noopener"
                                        className="block mt-1 text-un-lime text-xs underline underline-offset-2 truncate"
                                    >
                                        Preview
                                    </a>
                                )}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <input
                                    ref={certRef}
                                    type="file"
                                    accept="application/pdf,image/*"
                                    hidden
                                    data-testid="form-cert-input"
                                    onChange={(e) => handleFile(e.target.files?.[0], 'certificate')}
                                />
                                <button
                                    type="button"
                                    onClick={() => certRef.current?.click()}
                                    data-testid="form-cert-upload-btn"
                                    disabled={uploadingCert}
                                    className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-4 py-2 hover:bg-un-lime transition-colors disabled:opacity-60"
                                >
                                    {uploadingCert ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                                    {uploadingCert ? 'Uploading' : (form.certificateUrl ? 'Replace' : 'Upload certificate')}
                                </button>
                                {form.certificateUrl && (
                                    <button
                                        type="button"
                                        onClick={() => set({ certificateUrl: '' })}
                                        data-testid="form-cert-clear-btn"
                                        className="inline-flex items-center gap-1 rounded-full border border-white/20 text-white/80 text-sm px-3 py-2 hover:border-red-500 hover:text-red-400 transition-colors"
                                    >
                                        <X size={14} /> Clear
                                    </button>
                                )}
                            </div>
                        </Panel>
                    </div>

                    {/* Right col: fields */}
                    <div className="lg:col-span-2 space-y-6">
                        <Panel title="Basics">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="First name" required>
                                    <Input testid="form-firstName" value={form.firstName} onChange={(v) => set({ firstName: v })} />
                                </Field>
                                <Field label="Last name" required>
                                    <Input testid="form-lastName" value={form.lastName} onChange={(v) => set({ lastName: v })} />
                                </Field>
                                <Field label="Company name">
                                    <Input testid="form-companyName" value={form.companyName} onChange={(v) => set({ companyName: v })} />
                                </Field>
                                <Field label="Email">
                                    <Input testid="form-email" type="email" value={form.email} onChange={(v) => set({ email: v })} />
                                </Field>
                                <Field label="Year awarded">
                                    <Input
                                        testid="form-awardYear"
                                        type="number"
                                        value={form.awardYear ?? ''}
                                        onChange={(v) => set({ awardYear: v })}
                                        placeholder="e.g. 2026"
                                    />
                                </Field>
                            </div>
                        </Panel>

                        <Panel title="Phases earned">
                            <p className="text-xs text-white/50 mb-4">
                                Toggle each phase this recipient completed. Their total milestones are computed automatically from the phases below.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {CATEGORY_LIST.map((c) => {
                                    const active = form.awards.includes(c);
                                    const color = colorForCategory(c);
                                    const max = CATEGORY_MAX_MILESTONES[c];
                                    return (
                                        <button
                                            key={c}
                                            type="button"
                                            data-testid={`form-award-${categorySlug(c)}`}
                                            onClick={() => toggleAward(c)}
                                            className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] border transition-colors ${
                                                active ? '' : 'border-white/20 text-white/80 hover:border-white/60'
                                            }`}
                                            style={active ? { background: color, color: textOn(color), borderColor: color } : undefined}
                                            title={`${max} milestone${max === 1 ? '' : 's'}`}
                                        >
                                            {!active && (
                                                <span aria-hidden className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                                            )}
                                            {c}
                                            <span className={`ml-1 text-[10px] opacity-70 ${active ? '' : 'text-white/50'}`}>
                                                {max}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Auto total display */}
                            <div
                                data-testid="form-computed-total"
                                className="mt-6 flex items-center gap-4 rounded-2xl border border-white/10 bg-black px-5 py-4"
                            >
                                <span className="font-display font-black text-3xl text-un-lime">
                                    {form.awards.reduce((s, a) => s + (CATEGORY_MAX_MILESTONES[a] || 0), 0)}
                                </span>
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.22em] text-white/60">Total milestones (auto)</div>
                                    <div className="text-xs text-white/50">Sum of milestones across every phase earned.</div>
                                </div>
                            </div>
                        </Panel>

                        <Panel title="Bio">
                            <textarea
                                data-testid="form-bio"
                                value={form.bio}
                                onChange={(e) => set({ bio: e.target.value })}
                                rows={7}
                                placeholder={"Short story about the recipient — what they do, what they built, why they earned this.\n\nLeave a blank line between paragraphs. Use **word** for bold, *word* for italics, and [link text](https://example.com) for links."}
                                className="w-full rounded-2xl bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry font-body"
                            />
                            <p className="mt-2 text-[11px] text-white/40">
                                Formatting supported: blank lines split paragraphs · <code>**bold**</code> · <code>*italic*</code> · <code>[link](https://…)</code> · <code>- bullet lists</code>
                            </p>
                        </Panel>

                        <Panel title="Acceptance speech (optional)">
                            <label className="flex items-start gap-3 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    data-testid="form-has-speech-toggle"
                                    checked={!!form.hasAcceptanceSpeech}
                                    onChange={(e) => set({ hasAcceptanceSpeech: e.target.checked })}
                                    className="mt-1 h-4 w-4 accent-un-lime"
                                />
                                <span>
                                    <span className="block text-sm text-white font-semibold">
                                        Show acceptance speech video on this profile
                                    </span>
                                    <span className="block text-xs text-white/50 mt-1">
                                        When enabled, the video below appears on the public profile between the header and the certificate.
                                    </span>
                                </span>
                            </label>

                            {form.hasAcceptanceSpeech && (
                                <div className="mt-5">
                                    <label className="block text-[11px] uppercase tracking-[0.22em] text-white/70 mb-2">
                                        Video embed code
                                    </label>
                                    <textarea
                                        data-testid="form-speech-embed"
                                        value={form.acceptanceSpeechEmbed || ''}
                                        onChange={(e) => set({ acceptanceSpeechEmbed: e.target.value })}
                                        rows={5}
                                        placeholder='Paste an embed &lt;iframe …&gt; from YouTube, Vimeo, Loom, etc. Or paste a plain video URL (e.g. https://youtu.be/…) and we&apos;ll embed it for you.'
                                        className="w-full rounded-2xl bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry font-mono text-xs"
                                    />
                                    <p className="mt-2 text-[11px] text-white/40">
                                        Tip: On YouTube click <em>Share → Embed</em>, then copy the full <code>&lt;iframe&gt;</code> snippet.
                                    </p>
                                </div>
                            )}
                        </Panel>

                        <Panel title="Links">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="Website">
                                    <Input testid="form-link-website" value={form.links.website} onChange={(v) => setLink({ website: v })} placeholder="https://" />
                                </Field>
                                <Field label="Instagram">
                                    <Input testid="form-link-instagram" value={form.links.instagram} onChange={(v) => setLink({ instagram: v })} placeholder="https://instagram.com/…" />
                                </Field>
                                <Field label="LinkedIn">
                                    <Input testid="form-link-linkedin" value={form.links.linkedin} onChange={(v) => setLink({ linkedin: v })} placeholder="https://linkedin.com/in/…" />
                                </Field>
                                <Field label="X / Twitter">
                                    <Input testid="form-link-twitter" value={form.links.twitter} onChange={(v) => setLink({ twitter: v })} placeholder="https://x.com/…" />
                                </Field>
                            </div>
                        </Panel>
                    </div>
                </div>
            </form>
        </div>
    );
}

function Panel({ title, children }) {
    return (
        <div className="rounded-3xl border border-white/10 bg-neutral-950 p-5 md:p-6">
            <div className="text-xs uppercase tracking-[0.24em] text-white/50 mb-4">{title}</div>
            {children}
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-white/70 mb-2">
                {label} {required && <span className="text-un-berry">*</span>}
            </span>
            {children}
        </label>
    );
}

function Input({ testid, value, onChange, type = 'text', placeholder, min }) {
    return (
        <input
            data-testid={testid}
            type={type}
            value={value ?? ''}
            min={min}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-full bg-black border border-white/15 px-4 py-2.5 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry"
        />
    );
}
